import { Box, Typography, useTheme } from "@mui/material";
import { DarkDrawer } from "components/TradeDrawer";
import {
  MAX_PRIORITY_FEE,
  MAX_SLIPPAGE,
  MAX_VALIDATOR_TIP,
  MIN_PRIORITY_FEE,
  MIN_SLIPPAGE,
  MIN_VALIDATOR_TIP,
} from "consts";
import { useRef, useState } from "react";
import useUserStoreV2, {
  DEFAULT_CACHED_SLIPPAGE,
  DEFAULT_CACHED_VALIDATOR_TIP,
  DEFAULT_CACHED_PRIORITY_FEE,
} from "store/user-store-v2/useUserStoreV2";
import { StyledInputV2 } from "./styled";
import { RetroButton } from "components/RetroButton";
import axiosService from "services/axios";
import { CircleButton } from "components/CircleButton";
import { Close } from "@mui/icons-material";

const SLIPPAGE_OPTIONS = [
  {
    label: "0.5%",
    value: 0.5,
    isDefault: false,
  },
  {
    label: "1%",
    value: 1,
    isDefault: true,
  },
  {
    label: "5%",
    value: 5,
    isDefault: false,
  },
  {
    label: "10%",
    value: 10,
    isDefault: false,
  },
];

const PRIORITY_FEE_OPTIONS = [
  {
    label: "Economy",
    value: 0.0015,
    isDefault: false,
  },
  {
    label: "Standard",
    value: 0.015,
    isDefault: true,
  },
  {
    label: "Fast",
    value: 0.05,
    isDefault: false,
  },
];

const VALIDATOR_TIP_OPTIONS = [
  {
    label: "Low",
    value: 0.001,
    isDefault: false,
  },
  {
    label: "Medium",
    value: 0.01,
    isDefault: true,
  },
  {
    label: "High",
    value: 0.03,
    isDefault: false,
  },
];

export const PreferencesDrawer = ({
  isOpen,
  close,
  showPriorityFee,
  showValidatorTip,
}: {
  isOpen: boolean;
  close: () => void;
  showPriorityFee: boolean;
  showValidatorTip: boolean;
}) => {
  const {
    preferences = {
      slippage: DEFAULT_CACHED_SLIPPAGE,
      validatorTip: DEFAULT_CACHED_VALIDATOR_TIP,
      priorityFee: DEFAULT_CACHED_PRIORITY_FEE,
    },
    setPreferences,
  } = useUserStoreV2();
  const {
    slippage = DEFAULT_CACHED_SLIPPAGE,
    validatorTip = DEFAULT_CACHED_VALIDATOR_TIP,
    priorityFee = DEFAULT_CACHED_PRIORITY_FEE,
  } = preferences;

  const theme = useTheme();
  const [isSlippageValid, setIsSlippageValid] = useState(!!slippage);
  // const [isValidatorTipValid, setIsValidatorTipValid] = useState(!!validatorTip);
  // const [isPriorityFeeValid, setIsPriorityFeeValid] = useState(!!priorityFee);
  const [isShowTips, setIsShowTips] = useState(false);

  const slippageRef = useRef<HTMLInputElement>(null);
  const validatorTipRef = useRef<HTMLInputElement>(null);
  const priorityFeeRef = useRef<HTMLInputElement>(null);

  const handleSlippageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = parseFloat(event.target.value);
      slippageRef.current!.value = value === 0 ? event.target.value : value.toString();
      setIsSlippageValid(() => !isNaN(value) && value >= MIN_SLIPPAGE && value <= MAX_SLIPPAGE);
    } catch (error) {
      console.error(error);
    }
  };

  const handleValidatorTipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = parseFloat(event.target.value);
      validatorTipRef.current!.value = value === 0 ? event.target.value : value.toString();
      setIsShowTips(value > parseFloat(priorityFeeRef.current!.value));
      // setIsValidatorTipValid(
      //   () => !isNaN(value) && value >= MIN_VALIDATOR_TIP && value <= MAX_VALIDATOR_TIP,
      // );
    } catch (error) {
      console.error(error);
    }
  };

  const handlePriorityFeeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = parseFloat(event.target.value);
      priorityFeeRef.current!.value = value === 0 ? event.target.value : value.toString();
      setIsShowTips(parseFloat(validatorTipRef.current!.value) > value);
      // setIsPriorityFeeValid(
      //   () => !isNaN(value) && value >= MIN_PRIORITY_FEE && value <= MAX_PRIORITY_FEE,
      // );
    } catch (error) {
      console.error(error);
    }
  };

  const handleConfirmPreferences = async () => {
    try {
      if (!slippageRef.current && !validatorTipRef.current && !priorityFeeRef.current) {
        return;
      }

      const slippageValue = parseFloat(slippageRef.current?.value ?? slippage!.toString());
      const validatorTipValue = parseFloat(
        validatorTipRef.current?.value ?? validatorTip!.toString(),
      );
      const priorityFeeValue = parseFloat(priorityFeeRef.current?.value ?? priorityFee!.toString());
      await axiosService.updateUserPreferences({
        slippage: slippageValue,
        validatorTip: validatorTipValue,
        priorityFee: priorityFeeValue,
      });

      setPreferences(slippageValue, validatorTipValue, priorityFeeValue);
      close();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <DarkDrawer open={isOpen} anchor="bottom" onClose={() => {}} sx={{ zIndex: 2000 }}>
      {/* Slippage */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Typography variant="title-h2">Preferences</Typography>
        <Box>
          <CircleButton variant="white" size="lg" onClick={close}>
            <Close />
          </CircleButton>
        </Box>
      </Box>
      <Box sx={{ marginTop: "32px" }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Typography variant="subtitle">Slippage</Typography>
      </Box>
      <Box sx={{ marginTop: "8px" }}>
        <Box sx={{ position: "relative" }}>
          <StyledInputV2
            type="number"
            min={MIN_SLIPPAGE}
            max={MAX_SLIPPAGE}
            step="0.1"
            ref={slippageRef}
            onChange={handleSlippageChange}
            defaultValue={slippage}
          />
          <Typography
            variant="body-md"
            color={theme.palette.text.secondary}
            sx={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
            }}>
            %
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
        {SLIPPAGE_OPTIONS.map((option) => (
          <OptionButton
            key={option.value}
            label={option.label}
            onClick={() => {
              slippageRef.current!.value = option.value.toString();
              handleSlippageChange({
                target: { value: option.value.toString() },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            isDefault={option.isDefault}
          />
        ))}
      </Box>
      <Box sx={{ minHeight: "16px", height: "fit-content", opacity: 0, pointerEvents: "none" }}>
        <Typography variant="body-sm" color={theme.palette.text["system-red"]}>
          To ensure the transaction is successful please set the priority fee higher than the
          validator tip.
        </Typography>
      </Box>
      <Box sx={{ marginTop: "16px" }} />

      {/* Validator Tip */}
      {showValidatorTip ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Typography variant="subtitle">Validator Tip</Typography>
          </Box>
          <Box sx={{ marginTop: "8px" }}>
            <Box sx={{ position: "relative" }}>
              <StyledInputV2
                type="number"
                min={MIN_VALIDATOR_TIP}
                max={MAX_VALIDATOR_TIP}
                step="0.001"
                ref={validatorTipRef}
                onChange={handleValidatorTipChange}
                defaultValue={validatorTip}
              />
              <Typography
                variant="body-md"
                color={theme.palette.text.secondary}
                sx={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}>
                SOL
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
            {VALIDATOR_TIP_OPTIONS.map((option) => (
              <OptionButton
                key={option.value}
                label={option.label}
                onClick={() => {
                  validatorTipRef.current!.value = option.value.toString();
                  handleValidatorTipChange({
                    target: { value: option.value.toString() },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                isDefault={option.isDefault}
              />
            ))}
          </Box>
          <Box
            sx={{
              minHeight: "16px",
              height: "fit-content",
              opacity: isShowTips ? 1 : 0,
              pointerEvents: isShowTips ? "auto" : "none",
            }}>
            <Typography variant="body-sm" color={theme.palette.text["system-red"]}>
              To ensure the transaction is successful please set the priority fee higher than the
              validator tip.
            </Typography>
          </Box>
          <Box sx={{ marginTop: "16px" }} />
        </>
      ) : null}
      {/* Confirm */}
      <Box>
        <RetroButton
          variant="white"
          onClick={handleConfirmPreferences}
          disabled={!isSlippageValid /*  || !isValidatorTipValid || !isPriorityFeeValid */}>
          <Typography variant="title-h3" sx={{ paddingY: theme.spacing(1) }}>
            SAVE
          </Typography>
        </RetroButton>
      </Box>
      <Box
        sx={{
          marginTop: "64px",
        }}
      />
    </DarkDrawer>
  );
};

const OptionButton = ({
  label,
  onClick,
  isDefault,
}: {
  label: string;
  onClick: () => void;
  isDefault: boolean;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
      <RetroButton variant="primary" onClick={onClick}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 12px",
          }}>
          <Typography variant="button-md" sx={{ display: "inline-block", whiteSpace: "nowrap" }}>
            {label}
          </Typography>
        </Box>
      </RetroButton>
    </Box>
  );
};
